import { type Dispatch, type SetStateAction, useState, useCallback } from 'react';
import HTMLReactParser from 'html-react-parser';
import classNames from '@/utils/className';
import { DivContentNews } from '@/components/Internals/NewsContent/styles';
import { mglBlue, mglGray } from '@/styles/designSystemColors';
import Icon from '../Icon';

type AccordionItem = {
  title: string;
  content: string | JSX.Element;
};

interface AccordionProps {
  items: AccordionItem[];
  openItem?: string;
  blueTheme?: boolean;
}

export default function Accordion({ items, openItem, blueTheme }: AccordionProps) {
  const [selected, setSelected] = useState(openItem || '');
  return (
    <ul className={classNames('mx-4 my-0 list-none rounded bg-transparent px-8 tablet:mx-auto tablet:my-auto  tablet:mt-4', blueTheme && '  bg-white')}>
      {items.map(item => (
        <AccordionItem
          content={item.content}
          isOpen={item.title === selected}
          key={item.title}
          title={item.title}
          setSelected={setSelected}
          blueTheme={blueTheme}
        />
      ))}
    </ul>
  );
}

interface AccordionItemProps extends AccordionItem {
  isOpen: boolean;
  setSelected: Dispatch<SetStateAction<string>>;
  blueTheme?: boolean;
}

function AccordionItem({ isOpen, title, content, setSelected, blueTheme }: AccordionItemProps) {
  const isOpenProps = {
    Container: 'pb-6',
    Button: 'isOpenAccordion mb-4 [&>svg]:rotate-180',
  } as const;

  return (
    <li
      className={classNames(
        'border-0 border-b-[0.5px] border-solid border-b-mglGray200 bg-mglGray50 py-8',
        blueTheme && '  bg-white',
        isOpen && isOpenProps.Container
      )}>
      <button
        className={classNames(
          'flex w-full appearance-none items-center justify-between gap-8 border-none bg-mglGray50 [&>h2]:w-full [&>h2]:text-left [&>h2]:text-lg [&>svg]:transition-all [&>svg]:duration-300 [&>svg]:ease-ease',
          isOpen && isOpenProps.Button,
          blueTheme && 'bg-white',
          'transition-all duration-[600ms]'
        )}
        onClick={() => setSelected(isOpen ? '' : title)}>
        <h2>{title}</h2> <Icon icon="mdi:chevron-down" width={18} height={18} color={blueTheme ? mglBlue.mglBlue400 : mglGray.mglGray900} />
      </button>
      <DivContentNews
        className={classNames(
          'm-0 h-auto max-h-0 w-full overflow-hidden text-mglGray900 opacity-0 transition-all duration-[600ms] first:mt-2',
          isOpen && 'max-h-screen opacity-100'
        )}>
        {typeof content === 'object' ? content : HTMLReactParser(content)}
      </DivContentNews>
    </li>
  );
}
